import { useEventListener, useTimeoutFn } from "@vueuse/core";
import { onScopeDispose } from "vue";
import { settleConnected } from "#ce/apps/shared/composables/settle-connected";
import { isRegisteredTag } from "#ce/framework/registration";
import { REQUIRED_FILTERS_OPEN_EVENT } from "#ce/shared/finder-association";

/** A booting `sp-finder` keeps a viewport-tall box until it, or a finder inside it, carries this marker. */
export const FINDER_SHOWN_MARKER = "data-sp-finder-shown";

export const HOLD_CEILING_MS = 30_000;

const SEARCH_VIEW_TAG = "sp-finder-search-view";
const RESULT_VIEW_TAG = "sp-finder-result-view";
const SETTLE_PASSES = 3;
const SHOWN_INNER_FINDER = `sp-finder[${FINDER_SHOWN_MARKER}]`;
const BOOTING_FINDER = "sp-finder[boot]";

function nextFrame() {
  return new Promise((resolve) => window.requestAnimationFrame(resolve));
}

function inView(element) {
  return !element.closest("[hidden]") && element.getBoundingClientRect().top < window.innerHeight;
}

function catalogElementsInView(root) {
  return [...root.querySelectorAll("*")].filter((element) => isRegisteredTag(element.localName) && inView(element));
}

async function whenFinderStringsReady() {
  const [finderStrings, filterStrings] = await Promise.all([
    import("#ce/components/SpFinder/i18n"),
    import("#ce/shared/filters/i18n"),
  ]);

  await Promise.all([finderStrings.whenI18nReady, filterStrings.whenI18nReady]);
}

/**
 * Marks `host` shown once `finder` shows real content: the landing page, a finished search with or
 * without results, a failed search, or the required-filters dialog. Before marking it waits until:
 *   - every catalog element in view has mounted, failed or left the page
 *   - the finder and filter strings have loaded, when a mounted search view is in view
 *   - each mounted result view in view has rendered its results (`whenRendered`)
 *   - the fonts have loaded and two frames have painted
 * Elements that appear meanwhile are waited for too, up to `SETTLE_PASSES` passes. A wait that fails
 * is logged and ends the wait. `HOLD_CEILING_MS` after mounting it marks anyway, and warns unless a
 * finder inside it is already shown. Disposing the scope removes the mark. Does nothing unless
 * `host` is or sits inside a booting `sp-finder`, the box the page CSS holds.
 *
 * @param {HTMLElement} host
 * @param {ReturnType<typeof import("#ce/apps/SpFinder/store/finder-store").createFinderStore>} finder
 */
export function useFinderHold(host, finder) {
  if (!host.closest(BOOTING_FINDER)) {
    return;
  }

  let releasing = false;
  let disposed = false;

  useEventListener(host, REQUIRED_FILTERS_OPEN_EVENT, release);
  const { stop: stopCeiling } = useTimeoutFn(handleCeiling, HOLD_CEILING_MS);
  finder.subscribe(handleFinderState);

  function handleFinderState({ route, request, completed }) {
    if (route === "landing" || completed || request?.status === "error") {
      release();
    }
  }

  async function release() {
    if (releasing) {
      return;
    }

    releasing = true;

    try {
      await waitForContent();
    } catch (error) {
      console.error("sp-finder stopped waiting for its content", error);
    }

    show();
  }

  function handleCeiling() {
    if (!host.querySelector(SHOWN_INNER_FINDER)) {
      console.warn(`sp-finder released its box after ${HOLD_CEILING_MS} ms without its content`);
    }

    show();
  }

  function show() {
    stopCeiling();

    if (!disposed) {
      host.setAttribute(FINDER_SHOWN_MARKER, "");
    }
  }

  async function waitForContent() {
    await nextFrame();

    const settled = new Set();

    for (let pass = 0; pass < SETTLE_PASSES; pass++) {
      const elements = catalogElementsInView(host).filter((element) => !settled.has(element));

      if (!elements.length) {
        break;
      }

      elements.forEach((element) => settled.add(element));
      const mounted = (await Promise.all(elements.map(settleConnected))).filter(Boolean);

      if (mounted.some((element) => element.localName === SEARCH_VIEW_TAG)) {
        await whenFinderStringsReady();
      }

      await Promise.all(
        mounted.filter((element) => element.localName === RESULT_VIEW_TAG).map((element) => element.whenRendered()),
      );

      await nextFrame();
    }

    await document.fonts?.ready;
    await nextFrame();
    await nextFrame();
  }

  function forgetShown() {
    disposed = true;
    host.removeAttribute(FINDER_SHOWN_MARKER);
  }

  onScopeDispose(forgetShown);
}
